/**
 * Pure focus planners for entering, navigating, adding and dismissing:
 * which frames are presented, in which family, and their boxes. Chats lay
 * out in columns or a companion stack; other frames as rounds
 * (`focus-plan-rounds.ts`): the main frame large, the others beside it.
 */
import { calculateConversationFocusBoxes } from "@/lib/conversation-focus-layout-geometry";
import {
  MAX_FOCUS_FRAMES,
  type FocusWindowBox,
  type FocusWindowMode,
} from "@/lib/focus-window-layout";
import type {
  CustomFocusLayout,
  FocusCardOrigin,
  FocusRendererKind,
  PresentationState,
  PresentationVariant,
} from "@/lib/focus/focus-types";
import {
  focusMotion,
  isConversationFrame,
  planFocusBoxes,
  planFocusWindow,
  type FocusPlanEnv,
} from "@/lib/focus/focus-plan-layout";
import { planRemoveCompanionFrame } from "@/lib/focus/focus-plan-companion";
import {
  planFocusLayout,
  planFocusRound,
  planRoundLayout,
} from "@/lib/focus/focus-plan-rounds";
import { focusFamilyIds } from "@/lib/focus/focus-family";

export interface FocusEntry {
  variant: PresentationVariant;
  renderer: FocusRendererKind;
  displayId: number | null;
  frameIds: string[];
  startFrameId?: string;
  focusWindowMode?: FocusWindowMode | null;
  focusLayoutBoxes?: FocusWindowBox[];
  customFocusLayout?: CustomFocusLayout;
  agentContentFit?: PresentationState["agentContentFit"];
  preserveMixedFrameTypes?: boolean;
  /** The active frame reveals itself as it enters. */
  reveal?: boolean;
  /** The active frame grows from this wall card instead. */
  fromCard?: FocusCardOrigin;
  /** Review and deliverables show the active frame in this one box. */
  presentationBox?: FocusWindowBox;
}

export function planFocusEntry(
  entry: FocusEntry,
  env: FocusPlanEnv,
): PresentationState | null {
  const exists = (id: string) => env.frameType(id) !== undefined;
  const existingIds = entry.frameIds.filter(exists);
  const { startFrameId } = entry;
  if (
    startFrameId &&
    !existingIds.includes(startFrameId) &&
    exists(startFrameId)
  ) {
    existingIds.unshift(startFrameId);
  }
  if (existingIds.length === 0) return null;
  const startIndex = Math.max(
    0,
    startFrameId ? existingIds.indexOf(startFrameId) : 0,
  );
  const activeId = existingIds[startIndex]!;
  const focus = entry.variant === "focus";
  // An agent round arrives planned, its main frame first.
  const frameIds = focus
    ? entry.customFocusLayout?.kind === "rounds"
      ? existingIds
      : focusFamilyIds(
          existingIds,
          activeId,
          {
            companionStack:
              entry.customFocusLayout?.kind === "conversation_companion_stack",
            preserveMixedFrameTypes: entry.preserveMixedFrameTypes,
          },
          env,
        )
    : existingIds;
  const conversationBoxes =
    focus && frameIds.every((id) => isConversationFrame(env, id))
      ? calculateConversationFocusBoxes(env.surface, frameIds.length)
      : undefined;
  const state: PresentationState = {
    variant: entry.variant,
    renderer: entry.renderer,
    displayId: focus ? entry.displayId : null,
    frameIds,
    activeIndex: focus ? Math.max(0, frameIds.indexOf(activeId)) : startIndex,
    boxes: entry.presentationBox ? [entry.presentationBox] : [],
    naturalSizes: {},
    motion: entry.fromCard
      ? { frameId: activeId, seq: 1, kind: "from-card", ...entry.fromCard }
      : entry.reveal
        ? focusMotion(null, activeId, "reveal")
        : undefined,
    agentContentFit: conversationBoxes ? undefined : entry.agentContentFit,
    customFocusLayout:
      entry.customFocusLayout ??
      (conversationBoxes
        ? { kind: "conversation_columns", boxes: conversationBoxes }
        : undefined),
  };
  if (!focus) return state;
  const layoutBoxes =
    conversationBoxes ??
    entry.customFocusLayout?.boxes ??
    entry.focusLayoutBoxes;
  if (frameIds.length === 1 && entry.focusWindowMode && !layoutBoxes) {
    return planFocusWindow(state, activeId, entry.focusWindowMode, {}, env);
  }
  return planFocusLayout(
    state,
    activeId,
    {
      boxes: layoutBoxes,
      focusWindowMode: conversationBoxes ? null : entry.focusWindowMode,
      preserveCustomFocusLayout: !!(
        conversationBoxes ?? entry.customFocusLayout
      ),
    },
    env,
  );
}

/**
 * Show the frame at `index`: review and deliverables move to its box; a
 * focus only activates it, its layout and main frame stay.
 */
export function planPresentAt(
  state: PresentationState,
  input: { index: number; presentationBox?: FocusWindowBox },
): PresentationState | null {
  const index = Math.min(Math.max(input.index, 0), state.frameIds.length - 1);
  const activeId = state.frameIds[index];
  if (!activeId) return null;
  const direction =
    index > state.activeIndex
      ? "down"
      : index < state.activeIndex
        ? "up"
        : null;
  const next: PresentationState = {
    ...state,
    activeIndex: index,
    motion: direction ? focusMotion(state, activeId, direction) : state.motion,
  };
  return state.variant !== "focus" && input.presentationBox
    ? { ...next, boxes: [input.presentationBox] }
    : next;
}

/**
 * Add a frame to a running focus; families never mix. A chat joins the
 * columns; any other frame becomes the main one, the previous main tops
 * the column, and over four frames the oldest leaves.
 */
export function planAddFocusFrame(
  state: PresentationState,
  frameId: string,
  env: FocusPlanEnv,
): PresentationState | null {
  if (state.variant !== "focus" || env.frameType(frameId) === undefined) {
    return null;
  }
  const targetIsConversation = isConversationFrame(env, frameId);
  const familyIds = state.frameIds.filter(
    (id) =>
      env.frameType(id) !== undefined &&
      isConversationFrame(env, id) === targetIsConversation,
  );
  if (targetIsConversation) {
    const frameIds = familyIds.includes(frameId)
      ? familyIds
      : [...familyIds, frameId].slice(-MAX_FOCUS_FRAMES);
    const boxes = calculateConversationFocusBoxes(env.surface, frameIds.length);
    return planFocusBoxes(
      {
        ...state,
        frameIds,
        activeIndex: Math.max(0, frameIds.indexOf(frameId)),
        returnFrameId:
          familyIds.length === 0
            ? state.frameIds[state.activeIndex]
            : state.returnFrameId,
        agentContentFit: undefined,
        customFocusLayout: { kind: "conversation_columns", boxes },
      },
      frameId,
      { boxes, preserveCustomFocusLayout: true },
      env,
    );
  }
  if (familyIds.length === 0) {
    // Leaving a chat focus: the frame opens as its own window.
    return planFocusWindow(
      {
        ...state,
        frameIds: [frameId],
        activeIndex: 0,
        boxes: [],
        frameTags: undefined,
        returnFrameId: undefined,
        agentContentFit: undefined,
        customFocusLayout: undefined,
        focusWindow: undefined,
      },
      frameId,
      "normal",
      {},
      env,
    );
  }
  // A chat's companion stack leaves its chat behind.
  const family: PresentationState =
    familyIds.length === state.frameIds.length
      ? { ...state, agentContentFit: undefined }
      : {
          ...state,
          frameIds: familyIds,
          activeIndex: 0,
          returnFrameId: undefined,
          agentContentFit: undefined,
          customFocusLayout: undefined,
          focusWindow: undefined,
        };
  return planFocusRound(
    family,
    { frameIds: [frameId], owner: null, keep: true },
    env,
  );
}

export type FocusDismissal =
  | { kind: "exit" }
  | { kind: "change"; next: PresentationState };

/** Take a frame out of focus; the last frame or a stack's chat ends it. */
export function planDismissFocusFrame(
  state: PresentationState,
  frameId: string,
  env: FocusPlanEnv,
): FocusDismissal | null {
  if (state.variant !== "focus" || !state.frameIds.includes(frameId)) {
    return null;
  }
  const layout = state.customFocusLayout;
  if (layout?.kind === "conversation_companion_stack") {
    if (frameId === layout.conversationFrameId) return { kind: "exit" };
    const next = planRemoveCompanionFrame(state, frameId, env);
    if (next) return { kind: "change", next };
  }
  const frameIds = state.frameIds.filter(
    (id) => id !== frameId && env.frameType(id) !== undefined,
  );
  if (frameIds.length === 0) return { kind: "exit" };
  const previousActiveId = state.frameIds[state.activeIndex];
  const activeIndex = Math.max(0, frameIds.indexOf(previousActiveId ?? ""));
  if (layout?.kind === "conversation_columns") {
    const boxes = calculateConversationFocusBoxes(env.surface, frameIds.length);
    const next = planFocusBoxes(
      {
        ...state,
        frameIds,
        activeIndex,
        agentContentFit: undefined,
        customFocusLayout: { kind: "conversation_columns", boxes },
      },
      frameIds[activeIndex]!,
      { boxes, preserveCustomFocusLayout: true },
      env,
    );
    return next && { kind: "change", next };
  }
  // Closing the main makes the column's top the main; closing a side
  // frame keeps the main in its slot (`planRoundLayout`).
  const next = planRoundLayout({ ...state, frameIds, activeIndex }, env);
  return next && { kind: "change", next };
}
